/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2021 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import { Grid, Divider, Typography, Button } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

import types from "../../helpers/types";

import { fetchCarriers } from "../../slices/carrierManagementSlice";
import { postOrganization, putCarrierUpdate } from "../../api/api";
import CarrierOrganizationAutoComplete from "../common/CarrierOrganizationAutoComplete";
import { fetchOrganizations } from "../../slices/organizationsSlice";

function UpdateCarrierOrganization({
  setSuccessMessage,
  setUnsavedChanges,
  openedRow,
}) {
  const dispatch = useDispatch();
  const theme = useTheme();

  // carrier update form inputs
  const user = useSelector((state) => state.goTrackUser.goTrackUser);

  // carrier org input state
  const [hasUpdatedCarrierOrg, setHasUpdatedCarrierOrg] = useState(false);
  const [isCreate, setIsCreate] = useState(false);

  const [selectedOrganization, setSelectedOrganization] = useState({
    id: openedRow.organizationsId ?? "",
    label: openedRow.organizationName ?? "",
  });

  const [
    postNewOrganizationRequestStatus,
    setPostNewOrganizationRequestStatus,
  ] = useState(types.requestStatuses.initial);

  const [carrierOrgUpdateStatus, setCarrierOrgUpdateStatus] = useState(
    types.requestStatuses.initial
  );

  const [carrierOrganizationError, setCarrierOrganizationError] = useState(
    openedRow.organizationsId ? false : true
  );

  const isFetchingOrganizations = useSelector(
    (state) => state.organizations.isFetching
  );

  const handleOrganizationChange = (selection) => {
    setSelectedOrganization(selection);
    setUnsavedChanges("carrierOrg", true);
    setCarrierOrganizationError(false);
  };

  useEffect(() => {
    setSelectedOrganization({
      id: openedRow.organizationsId ?? "",
      label: openedRow.organizationName ?? "",
    });
    setHasUpdatedCarrierOrg(false);
    setCarrierOrganizationError(openedRow.organizationsId ? false : true);
  }, [openedRow]);

  const handleAssigningOrg = async () => {
    if (selectedOrganization.name || selectedOrganization.id) {
      let organizationId;
      if (selectedOrganization.id) {
        organizationId = selectedOrganization.id;
      } else {
        setPostNewOrganizationRequestStatus(types.requestStatuses.inProgress);
        try {
          const { data } = await postOrganization(selectedOrganization.label);
          setPostNewOrganizationRequestStatus(types.requestStatuses.success);
          organizationId = data;
          dispatch(fetchOrganizations());
        } catch {
          setPostNewOrganizationRequestStatus(types.requestStatuses.failure);
        }
      }

      setCarrierOrgUpdateStatus(types.requestStatuses.inProgress);
      try {
        const updates = {
          contactFullName: openedRow.contactFullName,
          carrierEmail: openedRow.email,
          email2: openedRow.email2,
          email3: openedRow.email3,
          carrierPhone1: openedRow.phone1,
          carrierPhone2: openedRow.phone2,
          carrierPhone3: openedRow.phone3,
          carrierTier: openedRow.tierName,
          organizationId,
          claimsEnabled: openedRow.claimsEnabled,
        };
        await putCarrierUpdate(openedRow.carrierId, updates);
        setCarrierOrgUpdateStatus(types.requestStatuses.success);
        setSuccessMessage("Carrier organization updated.");
        dispatch(fetchCarriers());
        setUnsavedChanges("carrierOrg", false);
        setHasUpdatedCarrierOrg(true);
      } catch {
        setCarrierOrgUpdateStatus(types.requestStatuses.failure);
      }
    } else {
      setCarrierOrganizationError(true);
    }
  };

  const carrierOrgUpdateInProgress =
    carrierOrgUpdateStatus === types.requestStatuses.inProgress ||
    postNewOrganizationRequestStatus === types.requestStatuses.inProgress;

  return (
    <Grid item xs={12}>
      <Grid container spacing={2}>
        <>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h4" style={{ fontWeight: "bold" }}>
              Carrier Organization
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <CarrierOrganizationAutoComplete
              selectedOrganization={selectedOrganization}
              setSelectedOrganization={handleOrganizationChange}
              error={carrierOrganizationError}
              disabled={
                carrierOrgUpdateInProgress ||
                isFetchingOrganizations ||
                hasUpdatedCarrierOrg
              }
              setIsCreate={setIsCreate}
              searchIconPosition="340px"
            />
          </Grid>
          <Grid item xs={3}>
            <Button
              onClick={handleAssigningOrg}
              style={{ marginTop: 10 }}
              disabled={carrierOrgUpdateInProgress || hasUpdatedCarrierOrg}
              data-testid="update-org-btn"
            >
              {(isCreate || !selectedOrganization?.id) &&
              selectedOrganization?.name &&
              !hasUpdatedCarrierOrg
                ? "CREATE"
                : "UPDATE"}
            </Button>
          </Grid>
        </>
      </Grid>
    </Grid>
  );
}

UpdateCarrierOrganization.propTypes = {
  setSuccessMessage: PropTypes.func.isRequired,
  setUnsavedChanges: PropTypes.func.isRequired,
  openedRow: PropTypes.object.isRequired,
};

export default UpdateCarrierOrganization;
